import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { PaidPlanCode, SubscriptionInfo } from '@/lib/types/subscription'
import { useSubscriptionInfo } from '@/lib/db/rxdb/hooks/entities/subscriptions'
import useLoginStore from '@/store/LoginStore'
import { getCurrentSubscription } from '@/lib/api/features/subscriptions/api/getCurrentSubscription'
import { logger } from '@/lib/monitoring/logger'

/**
 * Shape exposed by useSubscriptionContext. Inherits everything from
 * SubscriptionInfo (computed from synced RxDB) and adds the REST-only
 * trialEligibility map that /subscriptions/current returns alongside. It's not
 * synced because it doesn't need offline-first semantics.
 */
export interface SubscriptionContextValue extends SubscriptionInfo {
  /**
   * Per-paid-plan trial eligibility map from the REST current-subscription
   * endpoint. Undefined while the initial REST fetch is in flight or if it
   * failed; consumers should treat undefined as "unknown — show Upgrade,
   * never claim trial-eligible without confirmation".
   */
  trialEligibility?: Partial<Record<PaidPlanCode, boolean>>
  /**
   * Manually trigger a refetch of REST-only fields after a successful billing
   * operation (checkout return, plan change, cancel). Synced fields (plan,
   * subscription, usage) come from RxDB and don't need this.
   */
  refreshTrialEligibility: () => Promise<void>
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null)

/**
 * Subscription provider that manages subscription state and makes it
 * available throughout the app via context.
 */
export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const syncedInfo = useSubscriptionInfo()
  const setSubscriptionInfo = useLoginStore(
    (state: any) => state.setSubscriptionInfo,
  )

  const [trialEligibility, setTrialEligibility] =
    useState<Partial<Record<PaidPlanCode, boolean>> | undefined>(undefined)

  const refreshTrialEligibility = useCallback(async () => {
    try {
      const response = await getCurrentSubscription()
      setTrialEligibility(response.trialEligibility ?? {})
    } catch (error) {
      // Don't surface the error — trial eligibility is best-effort and only
      // affects CTA labelling. The synced subscription and plan in RxDB are
      // what gate real entitlements.
      logger.logDebug('[SubscriptionContext] refreshTrialEligibility failed', error)
    }
  }, [])

  // Fetch once on mount so the CTA can render correctly on first paint.
  useEffect(() => {
    void refreshTrialEligibility()
  }, [refreshTrialEligibility])

  // Sync to LoginStore for easy access outside React components.
  useEffect(() => {
    if (setSubscriptionInfo) {
      setSubscriptionInfo(syncedInfo)
    }
  }, [syncedInfo, setSubscriptionInfo])

  const value: SubscriptionContextValue = {
    ...syncedInfo,
    trialEligibility,
    refreshTrialEligibility,
  }

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

/**
 * Hook to access subscription context.
 * Throws if used outside SubscriptionProvider.
 */
export const useSubscriptionContext = (): SubscriptionContextValue => {
  const context = useContext(SubscriptionContext)
  if (!context) {
    throw new Error(
      'useSubscriptionContext must be used within SubscriptionProvider',
    )
  }
  return context
}
